import { useEffect, useRef, useState } from 'react';
import { ApiError, post } from '@yingbaba/api';

const unsettled = (status?: string) =>
  !!status && ['creating', 'pending', 'unknown', 'closing'].includes(status);

// The checkout owner remounts this hook when its account, organization or order changes.
export function useCheckoutRefresh(options: {
  kind: 'member' | 'digital';
  payment?: { id: string; status: string };
  receiptStatus?: string;
  orderStatus?: string;
  stopped: boolean;
  busy: boolean;
  refetch: () => Promise<{ error: unknown }>;
  onError: (error: unknown) => void;
  onStopped: () => void;
}) {
  const latest = useRef(options);
  latest.current = options;
  const alive = useRef(true);
  const pending = useRef(false);
  const [refreshing, setRefreshing] = useState(false);
  const needsQuery =
    !options.stopped &&
    !!options.orderStatus &&
    (options.receiptStatus === 'pending' ||
      (!options.receiptStatus &&
        (unsettled(options.payment?.status) ||
          options.payment?.status === 'paid' ||
          options.orderStatus === 'payment_review')));

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  async function refresh(manual = true) {
    const current = latest.current;
    if (!alive.current || pending.current || current.stopped || current.busy || document.hidden)
      return;
    pending.current = true;
    setRefreshing(true);
    try {
      // A receipt already proves payment. Only read fulfillment while the worker issues rights.
      if (
        current.payment &&
        ((!current.receiptStatus && unsettled(current.payment.status)) ||
          (manual && current.receiptStatus === 'pending'))
      )
        await post(`/${current.kind}/payments/${current.payment.id}/refresh`);
      if (!alive.current) return;
      const result = await current.refetch();
      if (!alive.current) return;
      if (result.error) throw result.error;
      current.onError(null);
    } catch (error) {
      if (alive.current) {
        current.onError(error);
        if (error instanceof ApiError && [401, 403, 404].includes(error.status))
          current.onStopped();
      }
    } finally {
      pending.current = false;
      if (alive.current) setRefreshing(false);
    }
  }

  useEffect(() => {
    if (!needsQuery) return;
    const first = window.setTimeout(() => void refresh(false), 100);
    const timer = window.setInterval(() => void refresh(false), 7000);
    const visible = () => {
      if (!document.hidden) void refresh(false);
    };
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [needsQuery, options.payment?.id]);

  return { refresh, refreshing, needsQuery };
}
